import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { HttpClient, HttpEventType, HttpRequest, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource, MatTab } from '@angular/material';
import { MediaObserver } from '@angular/flex-layout';

import { of, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, takeUntil, distinct, timeInterval } from 'rxjs/operators';
import { SessionStorage } from 'ngx-webstorage';

import {
    ApplicationService,
    CreateOptions,
    RequestInfo,
    SchoolInfo,
    CategoryInfo,
    FileAttachmentInfo,
    PersonInfo,
    ReferralOptions,
    RequestCommentInfo,
    SearchParameters,
} from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { DialogService, SelectBoxSelectionOption, SelectBoxNoteSelectionOption } from 'services/dialogs/dialogs.service';
import { DownloadService, HttpMethod, DownloadFormat } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService, UserInfo } from 'services/user/user.service';

import { UnsavedChangesService, UnsavedChanges } from 'routes/routes.guard';
import { FileUploadInformation } from 'modules/fileupload/fileupload.service';
import { BaseSubordinateDialogResult } from 'dialogs/subordinate/subordinate.dialogcomponent';
import { FileSizePipe } from 'pipes/filesize.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';

@Component({
    selector: 'app-handle-request',
    styleUrls: ['handlerequest.component.scss'],
    templateUrl: 'handlerequest.component.html',
})
export class HandleRequestComponent implements OnInit, OnDestroy, UnsavedChanges {
    @ViewChild('responseTA') private responseTextArea: ElementRef;
    private _subrelease: Subject<void> = new Subject<void>();

    ActionButtons: ActionButtons;
    AllCategories: CategoryInfo[];
    AllSchools: SchoolInfo[];
    AvailableSubordinates: PersonInfo[];
    BoardMembers: PersonInfo[];
    CanTakeActions: boolean;
    ChangesDetected: boolean;
    CommentsData: MatTableDataSource<RequestCommentInfo>;
    IsiPad: boolean;
    @SessionStorage() LastSearchOptions: SearchParameters;
    @SessionStorage() LastSearchResults: RequestInfo[];
    LoadedRequest: RequestInfo;
    OriginalRequestInfo: string;
    ParamsPreviousPage: string;
    ParamsRequestID: number;
    RequestForm: FormGroup;
    CommentsNoData: boolean;
    CommentsVisibleHeaders: string[];
    CommentsVisibleColumns: string[];
    ResponseMode: string;
    ResponseTabIndex: number;
    SelectedTabIndex: number;
    SmallSize: boolean;
    UnableToView: boolean;
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicationService,
        private configSvc: ConfigService,
        private dlgSvc: DialogService,
        private downloadSvc: DownloadService,
        private errorSvc: ErrorService,
        private fb: FormBuilder,
        private fileSizePipe: FileSizePipe,
        private http: HttpClient,
        private media: MediaObserver,
        private route: ActivatedRoute,
        private router: Router,
        private splashSvc: SplashService,
        private titleCasePipe: TitleCasePipe,
        private unsavedChangesSvc: UnsavedChangesService,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();

        // Stop tracking
        this.TakeDownTracking();
    }

    ngOnInit() {
        const self = this;

        self.AllCategories = null;
        self.AllSchools = null;
        self.AvailableSubordinates = null;
        self.BoardMembers = null;
        self.CanTakeActions = false;
        self.ChangesDetected = false;
        self.CommentsData = new MatTableDataSource<RequestCommentInfo>([{}]);
        // Set a flag if this is an iPad device, since those cannot do file attachment uploads
        self.IsiPad = window.navigator.userAgent.indexOf('iPad') >= 0 || window.navigator.userAgent.indexOf('iPhone') >= 0;
        self.LoadedRequest = null;
        self.CommentsNoData = false;
        self.CommentsVisibleColumns = [];
        self.CommentsVisibleHeaders = [];
        self.ResponseMode = null;
        self.ResponseTabIndex = 2;
        self.SelectedTabIndex = 0;
        self.UnableToView = false;
        self.User = null;
        self.ViewReady = false;

        // Wait for the first non-null user info object
        self.userSvc.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_u => !!_u && !!_u.AuthToken)
        ).subscribe(
            _user => {
                self.User = _user;

                // Initialize the form group
                self.BuildRequestFormGroup();

                // Subscribe to media size changes.
                self.media.media$.pipe(takeUntil(self._subrelease)).subscribe(m => {
                    self.SmallSize = m.mqAlias === 'xs' || m.mqAlias === 'sm';
                    self.CommentsVisibleHeaders = self.CommentsNoData
                        ? ['NoData']
                        : ['DateCommentAdded', 'Comment', 'CommentCreatorFullName', 'CommentRecipientFullName'];
                    self.CommentsVisibleColumns = self.SmallSize ? ['SmallSize'] : self.CommentsVisibleHeaders.slice();
                });

                // Subscribe to constant user changes, to keep AuthToken in sync.
                self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(
                    user => {
                        self.User = user;
                    },
                    error => self.errorSvc.HandleServiceError(error)
                );

                // Load data for component
                combineLatest(
                    self.appSvc.GetAllSchools(),
                    self.appSvc.GetAllCategories(),
                    self.appSvc.GetAllBoardMembers(),
                    ['DeputySuperintendent', 'Superintendent', 'Cabinet'].includes(self.User.CurrentRole)
                        ? self.appSvc.GetAllSubordinates(self.User.AuthToken, self.User.CurrentRole, self.User.SAPID)
                        : of<PersonInfo[]>([]),
                    self.route.params
                )
                    .pipe(
                        takeUntil(self._subrelease),
                        map(_p => {
                            return {
                                Schools: _p[0],
                                Categories: _p[1],
                                Board: _p[2],
                                Subordinates: _p[3],
                                Params: _p[4],
                            };
                        })
                    )
                    .subscribe(
                        packet => {
                            self.AllSchools = packet.Schools || [];
                            self.AllCategories = packet.Categories || [];
                            self.BoardMembers = packet.Board || [];
                            self.AvailableSubordinates = packet.Subordinates || [];
                            self.ParamsRequestID = +packet.Params.RequestID;
                            self.ParamsPreviousPage = packet.Params.PreviousPage;

                            // If we have a request ID, load it now so we can populate the form with the data from the request.
                            if (self.ParamsRequestID != null) {
                                self.appSvc
                                    .GetRequest(self.User.AuthToken, self.User.CurrentRole, self.ParamsRequestID, true)
                                    .subscribe(
                                        request => {
                                            // Update the form with the loaded request
                                            self.LoadedRequest = request;
                                            self.UpdateRequestForm(request);

                                            // Is the current user a Responder, and is the request loaded being accessed as a designee access?
                                            let cabOwnerSAPID = null;

                                            if (self.LoadedRequest.AsDesignee) {
                                                // Yes. We need to load "proper" subordinates of the cabinet member who is the current
                                                // owner/responder, so that the designee will be able to properly forward (if desired).
                                                const respRole = self.User.Roles.find(r => r.RoleName === 'Responder');
                                                const cabOwner =
                                                    respRole &&
                                                    respRole.OutOfOfficeSettings &&
                                                    respRole.OutOfOfficeSettings.find(
                                                        s =>
                                                            (s.Categories || []).findIndex(
                                                                c => c.CategoryID === self.LoadedRequest.CategoryID
                                                            ) >= 0
                                                    );

                                                cabOwnerSAPID =
                                                    (cabOwner && cabOwner.CabinetSAPID) ||
                                                    (self.LoadedRequest &&
                                                        self.LoadedRequest.ResponderInfo &&
                                                        self.LoadedRequest.ResponderInfo.SAPID) ||
                                                    (self.LoadedRequest && self.LoadedRequest.OwnerSAPID);
                                            }

                                            (cabOwnerSAPID
                                                ? self.appSvc.GetAllSubordinates(
                                                      self.User.AuthToken,
                                                      self.User.CurrentRole,
                                                      cabOwnerSAPID
                                                  )
                                                : of<PersonInfo[]>(self.AvailableSubordinates)
                                            ).subscribe(
                                                subs => {
                                                    self.AvailableSubordinates = subs || [];

                                                    // Set up form tracking by backing up the request form contents
                                                    self.OriginalRequestInfo = JSON.stringify(self.RequestForm.getRawValue());
                                                    self.ChangesDetected = false;
                                                    self.SetupTracking();
                                                    self.SetupButtons();

                                                    self.ResponseTabIndex =
                                                        2 +
                                                        (self.LoadedRequest.Comments && !!self.LoadedRequest.Comments.length ? 1 : 0);

                                                    // Set the current request
                                                    self.appSvc.CurrentRequest = {
                                                        FiscalYear: self.LoadedRequest.FiscalYear,
                                                        RequestID: self.LoadedRequest.RequestID,
                                                        IssueNumber: self.LoadedRequest.RequestIssueNumber,
                                                    };

                                                    self.ViewReady = true;
                                                },
                                                error => self.errorSvc.HandleServiceError(error)
                                            );
                                        },
                                        error => {
                                            // Is this an unauthorized access? If so, display a kinder message. Otherwise, fail out with an error splash.
                                            const errorInfo = self.errorSvc.RetrieveServiceError(error);

                                            if (
                                                errorInfo.ErrorMessage.startsWith('User is not authorized to view') ||
                                                errorInfo.ErrorMessage.includes('could not be located in the database')
                                            ) {
                                                self.UnableToView = true;
                                            } else {
                                                self.errorSvc.HandleServiceError(error);
                                            }

                                            self.ViewReady = true;
                                        }
                                    );
                            } else {
                                // Show the form with nothing loaded
                                self.ViewReady = true;
                            }
                        },
                        error => self.errorSvc.HandleServiceError(error)
                    );
            },
            error => self.errorSvc.HandleServiceError(error)
        );
    }

    // #region Add Board Note

    AddBoardNote() {
        const self = this;

        // Get the text to put in the message.
        self.dlgSvc
            .OpenTextNoteDialog({
                TitleText: 'Board Member Note',
                MessageText:
                    `Please enter the note you would like to leave for the board member in the space provided below. This note ` +
                    `will be available to the board member immediately, and the board member will be notified via email about a new note ` +
                    `for him/her in the system.`,
                OKText: 'Done',
                CancelText: 'Cancel',
            })
            .subscribe(note => {
                if (note == null || !note.Message) {
                    return;
                }

                // Note was provided. One final confirmation.
                self.dlgSvc
                    .OpenConfirmationDialog({
                        TitleText: 'Confirmation Requested',
                        MessageText:
                            `You are about to add the following note for the original board member:<br /><br />` +
                            `<em>${note.Message}</em><br /><br />When you click the "Add Note" button below, this note will be ` +
                            `added to the request, and the board member will be notified by email. Are you sure you wish to add ` +
                            `this note?`,
                        OKText: 'Yes - Add Note',
                        CancelText: 'No - Cancel',
                    })
                    .subscribe(_ => {
                        if (_ === true) {
                            // Send call to the service layer to add this note to the request record.
                            self.appSvc
                                .AddBoardNoteToRequest(
                                    self.User.AuthToken,
                                    self.User.CurrentRole,
                                    self.LoadedRequest.RequestID,
                                    note.Message
                                )
                                .subscribe(
                                    notes => {
                                        // Toast success.
                                        self.splashSvc.SnackbarSuccess('Note added to request successfully.');

                                        // Update the attached internal notes in the loaded request.
                                        self.LoadedRequest.Comments = notes || [];
                                        self.UpdateRequestForm(self.LoadedRequest);
                                    },
                                    error => self.errorSvc.HandleServiceError(error)
                                );
                        }
                    });
            });
    }

    // #endregion

    // #region Board Time Approve

    BoardTimeApprove() {
        const self = this;

        const action = 'Approval';
        const target = 'cabinet member';
        const verb = 'Approve';
        const pastverb = 'Approved';

        // Get a confirmation (and possible optional comment for the next recipient) before proceeding
        self.dlgSvc
            .OpenTimeApproveDialog({
                TitleText: `Confirm Time Request ${action}`,
                MessageText:
                    `You have elected to mark this request's approval for <strong>${
                        self.LoadedRequest.EstimatedJobTime
                    } hours of time</strong> as being <strong>approved</strong> by the school board. In the space provided ` +
                    `below, you may provide an <u>optional</u> brief message that will be visible to the ${target} ` +
                    `who receives this ${action.toLowerCase()}. You may also adjust/reset the priority level specified for this ` +
                    `request.`,
                OKText: `${verb} Time Request`,
                CancelText: 'Cancel',
                width: '600px',
                height: '500px',
                Priority: self.LoadedRequest.OriginalPriority,
                DueDate: self.LoadedRequest.DueDate,
            })
            .subscribe(result => {
                if (result != null) {
                    // User accepted. Send the request to the system.
                    self.splashSvc
                        .OpenSplash({ TitleText: 'Please Wait', MessageText: `Marking Time Request Board ${pastverb}...` })
                        .subscribe(() => {
                            self.appSvc
                                .MarkBoardTime(self.User.AuthToken, self.User.CurrentRole, {
                                    ReferralID: self.LoadedRequest.ReferralID,
                                    ResponderNotes: result.Message,
                                    TimeAction: 'APPROVE',
                                    Priority: result.Priority,
                                    DueDate: result.DueDate,
                                })
                                .subscribe(
                                    request => {
                                        // Splash success
                                        self.splashSvc.CloseSplash();
                                        self.splashSvc.SnackbarSuccess(
                                            `Request #${
                                                self.LoadedRequest.RequestIssueNumber
                                            } - Time Request Board ${pastverb} Successfully`
                                        );

                                        // If we have a previous page, return to it now.
                                        if (self.ParamsPreviousPage) {
                                            self.router.navigate([self.ParamsPreviousPage]);
                                        } else {
                                            self.router.navigate(['home']);
                                        }
                                    },
                                    error => self.errorSvc.HandleServiceError(error)
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Board Time Deny

    BoardTimeDeny(approval: boolean) {
        const self = this;

        // Get a confirmation (and possible optional comment for the next recipient) before proceeding
        self.dlgSvc
            .OpenTextNoteDialog({
                TitleText: `Confirm Time Request Denial`,
                MessageText:
                    `You have elected to mark this request's approval for <strong>${
                        self.LoadedRequest.EstimatedJobTime
                    } hours of time</strong> as being <strong>denied</strong> by the school board. This action will close this referral. ` +
                    `In the space provided below, you may provide an <u>optional</u> brief message that will be visible to the board member ` +
                    ` who receives this denial.`,
                OKText: `Deny Time Request`,
                CancelText: 'Cancel',
                width: '600px',
                height: '300px',
            })
            .subscribe(result => {
                if (result != null) {
                    // User accepted. Send the request to the system.
                    self.splashSvc
                        .OpenSplash({ TitleText: 'Please Wait', MessageText: `Marking Time Request Board Denied...` })
                        .subscribe(() => {
                            self.appSvc
                                .MarkBoardTime(self.User.AuthToken, self.User.CurrentRole, {
                                    ReferralID: self.LoadedRequest.ReferralID,
                                    ResponderNotes: result.Message,
                                    TimeAction: 'DISAPPROVE',
                                })
                                .subscribe(
                                    request => {
                                        // Splash success
                                        self.splashSvc.CloseSplash();
                                        self.splashSvc.SnackbarSuccess(
                                            `Request #${
                                                self.LoadedRequest.RequestIssueNumber
                                            } - Time Request Board Denied Successfully`
                                        );

                                        // If we have a previous page, return to it now.
                                        if (self.ParamsPreviousPage) {
                                            self.router.navigate([self.ParamsPreviousPage]);
                                        } else {
                                            self.router.navigate(['home']);
                                        }
                                    },
                                    error => self.errorSvc.HandleServiceError(error)
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Build Request Form Group

    BuildRequestFormGroup() {
        const self = this;

        // Build the request form
        self.RequestForm = self.fb.group({
            RequestID: [null],
            Subject: ['', Validators.required],
            RequestCategory: [null, Validators.required],
            DueDate: [null, Validators.required],
            Priority: [null, Validators.required],
            Confidential: [false],
            Source: [0, Validators.required], // For now, only board requests possible (code 0) [self.User.CurrentRole === 'BoardMember' ? 0 : null],
            SourceDescription: [''],
            RequestBoardMember: [null, Validators.required],
            SchoolNumber: [''],
            BoardDistrict: [''],
            RequestText: ['', Validators.required],
            RequestIssueNumber: [null, Validators.required],
            RequestAttachments: self.fb.array([]),
            RequestedBySAPID: ['', Validators.required],
            Response: [''],
            ResponseConfidential: [false],
            SourceNotes: [''],
            ReferralID: [null],
            CommentsFromSource: self.fb.array([]),
            CommentsFromResponder: self.fb.array([]),
            Comments: self.fb.array([]),
            EstimatedJobTime: [null],
            ActualJobTime: [null],
            Responses: self.fb.array([]),
            ResponseAttachments: self.fb.array([]),
            ResponseUploadAttachments: self.fb.array([]),
            FollowUpResponse: ['', Validators.maxLength(5000)],
            FollowUpAttachments: self.fb.array([]),
            FollowUpUploadAttachments: self.fb.array([]),
        });

        // #region Setup request subscriptions

        self.RequestForm.valueChanges
            .pipe(
                takeUntil(self._subrelease),
                debounceTime(250),
                distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y))
            )
            .subscribe(value => {
                self.ChangesDetected = JSON.stringify(value) !== self.OriginalRequestInfo;
            });

        // #endregion
    }

    // #endregion

    // #region Can Deactivate

    CanDeactivate(liveCheck?: boolean) {
        const self = this;

        if (!self.RequestForm) {
            return true;
        }

        // Are there unsaved changes?
        if (!self.ChangesDetected) {
            // No. The user can deactivate.
            return true;
        }

        // If this is a live check (native from browser), forego the confirmation dialog as the native
        // browser will catch instead.
        if (liveCheck === true) {
            return false;
        }

        // Need to prompt
        return self.dlgSvc.OpenConfirmationDialog({
            TitleText: 'Unsaved Changes Detected',
            MessageText:
                `The request form appears to have unsaved changes which have not yet been submitted ` +
                `to the system. If you leave this page now, your unsaved changes will be lost. Do you wish ` +
                `to discard your changes and leave the page?`,
            OKText: 'Yes - Discard Changes',
            CancelText: 'No - Stay On Page',
        });
    }

    // #endregion

    // #region Fork Request

    ForkRequest() {
        const self = this;

        // If the current user isn't a developer or facilitator, fail out.
        if (!['Developer', 'Facilitator'].includes(self.User.CurrentRole)) {
            return;
        }

        // If the current request isn't in a finalized state, fail out.
        if (!['DENIED', 'UNAVAIL', 'COMPLETED', 'CLOSEDNONINFO'].includes(self.LoadedRequest.Status)) {
            return;
        }

        // Get a confirmation to proceed to the create request form.
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to navigate to the "Create Request" screen to create a related request to this closed request. ` +
                    `When the form loads, certain information will be pre-poulated for you, such as the board member who requested ` +
                    `the original request you are currently viewing. Are you sure you wish to proceed to the "Create Request" ` +
                    `screen at this time?`,
                OKText: 'Yes - Proceed',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (!result) {
                    return;
                }

                // Navigate over to the create request route, but send along this request's ID value as a parameter.
                self.router.navigate(['/create', self.LoadedRequest.RequestID]);
            });
    }

    // #endregion

    // #region Forward Referral

    ForwardReferral(second?: PersonInfo) {
        const self = this;

        // Is the current user a cabinet member, deputy, or super? If not, they must be a responder and the current request must be AsDesignee. Otherwise, they cannot forward.
        if (
            !self.User ||
            !(
                ['Cabinet', 'DeputySuperintendent', 'Superintendent'].includes(self.User.CurrentRole) ||
                (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)
            )
        ) {
            return;
        }

        // Pop up a dialog to gather the name of the subordinate this cabinet member will be forwarding a request to, unless we are provided
        // a second SAPID to send it to.
        (second
            ? self.dlgSvc
                  .OpenTextNoteDialog({
                      TitleText: `Return to ${second.FirstName} ${second.LastName}`,
                      MessageText:
                          `You are about to return this referral to ${second.FirstName} ${second.LastName}. Before you ` +
                          `forward this referral, you may use the provided space below to enter any optional comments you ` +
                          `wish to attach to this forwarded referral. These optional comments will only be seen by the ` +
                          `recipient.`,
                      NoteRequired: false,
                      PlaceholderText: 'Enter optional comments here...',
                      OKText: 'Return Referral',
                      CancelText: 'Cancel',
                  })
                  .pipe(
                      map(nresult => {
                          if (nresult == null) {
                              return null;
                          }
                          return <BaseSubordinateDialogResult>{
                              ReferralID: self.LoadedRequest.ReferralID,
                              Message: nresult.Message,
                              Selection: second.SAPID,
                          };
                      })
                  )
            : self.dlgSvc.OpenSubordinateDialog({
                  TitleText: 'Select Referral Recipient',
                  MessageText:
                      `Use the selection list below to select a recipient for this referral forwarding. This list ` +
                      `contains all direct and indirect subordinates for your position. Once you have selected a recipient, click the ` +
                      `"Forward Referral" button to forward this request to the selected subordinate for a response.`,
                  OKText: 'Forward Referral',
                  CancelText: 'Cancel',
                  Options: self.AvailableSubordinates.map(
                      s =>
                          <SelectBoxSelectionOption>{
                              Text: `${s.FullName} (${s.LocalJobTitle})`,
                              Value: s.SAPID,
                          }
                  ),
                  Placeholder: 'Select referral recipient...',
                  TextPrefix: 'Enter optional comments to this recipient here. These will only be seen by this person:',
                  width: '550px',
                  height: '450px',
              })
        ).subscribe(result => {
            if (result != null) {
                // User selected a recipient. Forward this referral on to the recipient.
                self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Forwarding Referral...' }).subscribe(() => {
                    self.appSvc
                        .ReferralForwardToRecipient(self.User.AuthToken, self.User.CurrentRole, {
                            ReferralID: self.LoadedRequest.ReferralID,
                            ResponderNotes: result.Message,
                            RecipientSAPID: result.Selection,
                        })
                        .subscribe(
                            request => {
                                // Snackbar the success
                                self.splashSvc.CloseSplash();
                                self.splashSvc.SnackbarSuccess('Referral Forwarded To Responder Successfully.', 3000);

                                // If we have a previous page, go there now.
                                self.router.navigate([self.ParamsPreviousPage || 'home']);
                            },
                            error => self.errorSvc.HandleServiceError(error)
                        );
                });
            }
        });
    }

    // #endregion

    // #region Go To Related

    GoToRelated(com: RequestCommentInfo) {
        const self = this;

        if (!com) {
            return;
        }

        // We need to get the request ID attached to the provided comment link. Fetch the link now.
        self.appSvc
            .GetRelatedRequest(self.User.AuthToken, self.User.CurrentRole, com.RelatedRequestLinkYear, com.RelatedRequestLinkNumber)
            .subscribe(
                rel => {
                    // We have it. Route there now.
                    if (rel != null) {
                        self.router.navigate(['/handlerequest', rel]);
                    }
                },
                error => self.errorSvc.HandleServiceError(error)
            );
    }

    // #endregion

    // #region Handle Cancel Request

    HandleCancelRequest() {
        const self = this;

        if (!self.LoadedRequest) {
            return;
        }

        // Get a confirmation before proceeding
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'Cancellation Confirmation Requested',
                MessageText:
                    `You are about to cancel this request in the IRIS system:<br /><br />` +
                    `<strong>Subject: </strong>${self.LoadedRequest.Subject}<br /><br />Once cancelled, this ` +
                    `request cannot be reactivated. Are you sure you wish to proceed?`,
                OKText: 'Yes - Cancel Request',
                CancelText: 'No - Do Nothing',
            })
            .subscribe(result => {
                if (result === true) {
                    // User agreed. Cancel this request by setting the status code to 'CANCEL' and setting a default message.
                    self.LoadedRequest.Status = 'CANCEL';
                    self.LoadedRequest.RequestCloseNote = 'This request was cancelled in the system. No further action is necessary.';

                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Cancelling request...' }).subscribe(() => {
                        self.appSvc.UpdateRequestStatus(self.User.AuthToken, self.User.CurrentRole, self.LoadedRequest).subscribe(
                            updatedRequest => {
                                // Drop splash and snackbar success
                                self.splashSvc.CloseSplash();
                                self.splashSvc.SnackbarSuccess(
                                    `Request #${self.LoadedRequest.RequestIssueNumber} Cancelled Successfully.`
                                );

                                // If there is a known previous page, return to it now.
                                if (self.ParamsPreviousPage) {
                                    self.router.navigate([self.ParamsPreviousPage]);
                                }
                            },
                            error => self.errorSvc.HandleServiceError(error)
                        );
                    });
                }
            });
    }

    // #endregion

    // #region Handle File Attachment

    HandleFileAttachment(file: FileAttachmentInfo) {
        if (!file) {
            return;
        }

        const self = this;

        // Download the selected file
        self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Downloading file...' }).subscribe(() => {
            self.downloadSvc
                .RetrieveDownload(
                    HttpMethod.GET,
                    `Api/Services/IRIS/DownloadFile`,
                    {
                        AuthToken: self.User.AuthToken || '',
                        FileName: file.FileName,
                        FilePath: file.SharepointURL,
                    },
                    DownloadFormat.Any
                )
                .subscribe(
                    () => {
                        self.splashSvc.CloseSplash();
                    },
                    error => self.errorSvc.HandleServiceError(error)
                );
        });
    }

    // #endregion

    // #region Handle Info Unavailable

    HandleInfoUnavailable() {
        const self = this;

        // Determine the general name for who this will go to. If this is a cabinet member, it will go to the facilitator. Otherwise, we can simply assume it's
        // a supervisor
        const target =
            self.User.CurrentRole === 'Cabinet' || (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)
                ? 'the facilitator'
                : self.User.CurrentRole === 'Facilitator'
                ? 'the board member'
                : 'your supervisor';

        // Has a response message of any kind been entered into the request's response panel? If not, alert and back out, as that has to be present.
        if (!self.RequestForm.get('Response').value && self.ActionButtons.MarkUnavailable) {
            self.dlgSvc
                .OpenMessageBoxDialog({
                    TitleText: 'Response Requested',
                    MessageText: `You have not provided a response message to this request referral. Prior to marking this referral as having no information available, a response message must be entered into the provided box. Please complete the response box and try again.`,
                })
                .subscribe(() => {
                    // Move the panel to the response panel, and focus the textbox?
                    self.SelectedTabIndex = self.ResponseTabIndex;
                    if (self.responseTextArea != null) {
                        window.setTimeout(() => {
                            self.responseTextArea.nativeElement.focus();
                        }, 0);
                    }
                });
            return;
        }

        // Pop up the dialog box to try and collect the amount of hours needed to complete the job (estimate)
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'Confirm Information Is Unavailable',
                MessageText: `You have elected to mark this request as having no information available. This action will close this referral and return your response to ${target}. If you wish to change your response prior to sending this referral back to ${target}, please click "Cancel" and modify your response at this time. Otherwise, click "Mark Unavailable" to continue.`,
                OKText: 'Mark Unavailable',
                CancelText: 'Cancel',
                width: '600px',
                height: '300px',
            })
            .subscribe(result => {
                if (result === true) {
                    // User accepted. Send the request to the system.
                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Marking Unavailable...' }).subscribe(() => {
                        self.appSvc
                            .ReferralMarkUnavailable(self.User.AuthToken, self.User.CurrentRole, {
                                ReferralID: self.LoadedRequest.ReferralID,
                                Response: self.RequestForm.get('Response').value,
                                Confidential: self.RequestForm.get('ResponseConfidential').value,
                            })
                            .subscribe(
                                request => {
                                    // Splash success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess(
                                        `Request #${
                                            self.LoadedRequest.RequestIssueNumber
                                        } - Marked Information Unavailable Successfully`
                                    );

                                    // If we have a previous page, return to it now.
                                    if (self.ParamsPreviousPage) {
                                        self.router.navigate([self.ParamsPreviousPage]);
                                    } else {
                                        self.router.navigate(['home']);
                                    }
                                },
                                error => self.errorSvc.HandleServiceError(error)
                            );
                    });
                }
            });
    }

    // #endregion

    // #region Handle Search Return

    HandleSearchReturn() {
        const self = this;

        // Navigate back to the search page
        self.router.navigate(['search']);
    }

    // #endregion

    // #region Handle Submit Response

    HandleSubmitResponse() {
        const self = this;

        const form = self.RequestForm.getRawValue();

        if (!form.Response && self.ActionButtons.SubmitResponse) {
            self.dlgSvc
                .OpenMessageBoxDialog({
                    TitleText: 'No Response Text',
                    MessageText: `You have not provided a response message. Please use the "Resolution / Response" panel to provide a response to this request, and then try again.`,
                    OKText: 'OK',
                })
                .subscribe();
            return;
        }

        // Determine the general name for who this will go to. If this is a cabinet member, it will go to the facilitator. Otherwise, we can simply assume it's
        // a supervisor
        const target =
            self.User.CurrentRole === 'Cabinet' || (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)
                ? 'the facilitator'
                : self.User.CurrentRole === 'Facilitator'
                ? 'the requesting board member'
                : 'your supervisor';

        // Get a confirmation before proceeding.
        self.dlgSvc
            .OpenTimeRequestDialog({
                TitleText: 'Submit Response Confirmation',
                MessageText:
                    `You are about to submit your response to this request referral. If you would like to revise your ` +
                    `response, or attach any additional files, please click the "Cancel" button and complete your work.<br /><br />In the space ` +
                    `provided below, ${
                        self.ActionButtons.PushResponse && self.LoadedRequest.ActualJobTime != null
                            ? 'the previously provided actual amount of job time (<b>hours</b>) ' +
                              `spent for this request has been entered${
                                  self.User.CurrentRole !== 'Facilitator' ? ', and may be revised prior to submission' : ''
                              }.`
                            : 'please provide the actual amount ' + 'of job time (<b>hours</b>) that was spent for this request.'
                    }${
                        self.User.CurrentRole !== 'Facilitator'
                            ? ` Please enter ` + `only a numerical value (such as 2, 3.5, etc).`
                            : ''
                    }<br /><br />You may also provide ` +
                    `an optional brief message that will only be seen by ${target} regarding this response.<br /><br />`,
                Validators: [Validators.pattern(/^\d*\.?\d*$/)],
                Value:
                    self.ActionButtons.PushResponse && self.LoadedRequest.ActualJobTime != null
                        ? self.LoadedRequest.ActualJobTime
                        : null,
                InputHint: 'Numbers only, please.',
                InputPrefix: 'Actual Hours:',
                TextboxPrefix: `Optional comment(s) to ${target}:`,
                TimeEditable: self.User.CurrentRole !== 'Facilitator',
                OKText: 'Submit Response',
                CancelText: 'CANCEL',
                width: '800px',
                height: '550px',
            })
            .subscribe(result => {
                if (result != null) {
                    // Submit the response at this time.
                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Uploading Response...' }).subscribe(() => {
                        // Send the response to the server. Begin by packing the request object that will be sent to the API.
                        const actualTime = result.JobTime;
                        const message = result.OptionalComment;

                        const formData = new FormData();

                        // Store all files in the form data
                        for (const file of form.ResponseUploadAttachments) {
                            formData.append('file', file.File, file.File.name);
                        }

                        // Set the parameters now for the call: AuthToken, CurrentRole, IRISReferralID, etc.
                        formData.append('AuthToken', self.User.AuthToken || '');
                        formData.append('CurrentRole', self.User.CurrentRole || '');
                        formData.append('Source', 'R');
                        formData.append(
                            'infoObject',
                            JSON.stringify({
                                ReferralID: form.ReferralID,
                                ActualJobTime: actualTime,
                                ResponderNotes: message,
                                Response: form.Response,
                                Confidential: form.ResponseConfidential != null ? form.ResponseConfidential : '',
                            })
                        );

                        // Set the http Headers
                        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

                        // Create an http-post request and pass the form, while telling the request to report upload
                        // progress.
                        const req = new HttpRequest(
                            'POST',
                            self.configSvc.hostAddress + 'Api/Services/IRIS/ReferralSubmitResponse',
                            formData,
                            {
                                //                headers: headers,
                                reportProgress: true,
                            }
                        );

                        // Send the http-post request and subscribe for progress updates on it.
                        this.http.request(req).subscribe(
                            event => {
                                if (event.type === HttpEventType.UploadProgress) {
                                    // Calculate the progress percentage.
                                    const percentDone = Math.round((100 * event.loaded) / event.total);
                                    // Update the progress BehaviorSubject with the new calculation.
                                    // const newentry = {...progress.value};
                                    // newentry.PercentComplete = percentDone;

                                    // progress.next(newentry);
                                } else if (event instanceof HttpResponse) {
                                    // Close the progress BehaviorSubject as the upload is now complete.
                                    // const oldval = {...progress.value};
                                    // oldval.isComplete = true;
                                    // oldval.isInProgress = false;

                                    // Set the final file information so the client can update the UI.
                                    const requestInfo = <any>event.body;
                                    // oldval.FinalFileInfo = (fileInfo && fileInfo[0]);

                                    // if (oldval.FinalFileInfo) {
                                    //     oldval.FinalFileInfo.DateUploaded = (oldval.FinalFileInfo.DateUploaded ?
                                    //         new Date(oldval.FinalFileInfo.DateUploaded) : null);
                                    // }

                                    // Send out one more update before closing it.
                                    // progress.next(oldval);
                                    // progress.complete();

                                    // Splash the success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess(`Response Submitted Successfully.`);

                                    // If we have a previous page, go to it. Otherwise, go home.
                                    if (self.ParamsPreviousPage) {
                                        self.router.navigate([self.ParamsPreviousPage]);
                                    } else {
                                        self.router.navigate(['home']);
                                    }
                                }
                            },
                            error => {
                                // const oldval = {...progress.value};
                                // oldval.Error = error.error;
                                // oldval.isError = true;
                                // oldval.isInProgress = false;

                                // progress.error(oldval);
                                self.errorSvc.HandleServiceError(error);
                            },
                            () => {}
                        );
                    });
                }
            });
    }

    // #endregion

    // #region Handle Time Request

    HandleTimeRequest() {
        const self = this;

        // Determine the general name for who this will go to. If this is a cabinet member, it will go to the facilitator. Otherwise, we can simply assume it's
        // a supervisor
        const target =
            self.User.CurrentRole === 'Cabinet' || (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)
                ? 'the facilitator'
                : self.User.CurrentRole === 'Facilitator'
                ? 'the requesting board member'
                : 'your supervisor';

        // Pop up the dialog box to try and collect the amount of hours needed to complete the job (estimate)
        self.dlgSvc
            .OpenTimeRequestDialog({
                TitleText: 'Request Time Approval',
                MessageText: `You have elected to request approval for job time necessary to completing this request. In the space provided below, ${
                    self.ActionButtons.PushRequestTime && self.LoadedRequest.EstimatedJobTime != null
                        ? 'the previously provided estimated amount of job time (<b>hours</b>) ' +
                          `indicated for this request has been entered${
                              self.User.CurrentRole !== 'Facilitator' ? ', and may be revised prior to submission' : ''
                          }.`
                        : 'please provide the estimated amount of job time (<b>hours</b>) to be spent for this request.'
                }${
                    self.User.CurrentRole !== 'Facilitator' ? ` Please enter only a numerical value (such as 2, 3.5, etc).` : ''
                }<br /><br />You may also provide an optional brief message that will only be seen by ${target} explaining the time approval request.`,
                Validators: [Validators.pattern(/^\d*\.?\d*$/)],
                Value:
                    self.ActionButtons.PushRequestTime && self.LoadedRequest.EstimatedJobTime != null
                        ? self.LoadedRequest.EstimatedJobTime
                        : null,
                InputHint: 'Numbers only, please.',
                InputPrefix: 'Estimated Hours:',
                TextboxPrefix: `Optional comment(s) to ${target}:`,
                TimeEditable: self.User.CurrentRole !== 'Facilitator',
                OKText: 'Submit Time Request',
                CancelText: 'Cancel',
                width: '800px',
                height: '500px',
            })
            .subscribe(result => {
                if (result != null) {
                    // User accepted. Retrieve the time from the data object returned
                    const timeNeeded = result.JobTime;
                    const message = result.OptionalComment;

                    // Update the referral by sending the job time back with the referral to the person who forwarded
                    // this referral to this person. (If this is a cabinet member, it'll go to the facilitator; if it's
                    // a responder, it'll go to the cabinet member instead.)
                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Sending Time Request...' }).subscribe(() => {
                        self.appSvc
                            .ReferralRequestTime(self.User.AuthToken, self.User.CurrentRole, {
                                ReferralID: self.LoadedRequest.ReferralID,
                                EstimatedJobTime: timeNeeded,
                                ResponderNotes: message,
                            })
                            .subscribe(
                                request => {
                                    // Splash success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess(
                                        `Request #${
                                            self.LoadedRequest.RequestIssueNumber
                                        } - Request for Time Approval Submitted Successfully`
                                    );

                                    // If we have a previous page, return to it now.
                                    if (self.ParamsPreviousPage) {
                                        self.router.navigate([self.ParamsPreviousPage]);
                                    } else {
                                        self.router.navigate(['home']);
                                    }
                                },
                                error => self.errorSvc.HandleServiceError(error)
                            );
                    });
                }
            });
    }

    // #endregion

    // #region Handle Upload Attachment

    HandleUploadAttachment(files: FileUploadInformation[], targetList: boolean) {
        if (!files || !files.length) {
            return;
        }

        const self = this;

        // Add each file selected to the form array, if it is not already scheduled for uploading.
        const listFArray = (targetList === true
            ? self.RequestForm.get('FollowUpUploadAttachments')
            : self.RequestForm.get('ResponseUploadAttachments')) as FormArray;
        const reqUploadsArr = listFArray.getRawValue() as FileUploadInformation[];

        const reqUploads = (reqUploadsArr || ([] as FileUploadInformation[])).map(f => f.File.name);
        const badFiles = [] as File[];

        // Add each file selected to the form array
        files.forEach(file => {
            const fileName = file.File.name;

            // Is this file already scheduled for uploading? If it is, we need to park the info in the badFiles array,
            // so we can alert afterwards. Otherwise, put it in the queue to be uploaded.
            if (reqUploads.includes(fileName)) {
                badFiles.push(file.File);
            } else {
                // We can include this file.
                const fileGp = self.fb.group({
                    File: [file.File],
                });

                listFArray.push(fileGp);

                // Store the file info for reference in this loop.
                reqUploads.push(fileName);
            }
        });

        // If any "bad" files were encountered, pop up an alert about it now, to let the user now.
        if (badFiles.length) {
            self.dlgSvc
                .OpenMessageBoxDialog({
                    TitleText: `Duplicate File${badFiles.length > 1 ? 's' : ''} Detected`,
                    MessageText:
                        `The following file${badFiles.length > 1 ? 's' : ''} selected for upload ` +
                        `${badFiles.length > 1 ? 'were' : 'was'} omitted because ${
                            badFiles.length > 1 ? 'they have' : 'it has'
                        } the ` +
                        `same file name as one or more files you already have scheduled for uploading to this request record:` +
                        `<br /><br />${badFiles.reduce(
                            (prev, cur) =>
                                prev + `<div><strong>${cur.name} (${self.fileSizePipe.transform(cur.size, 1)})</strong></div>`,
                            ''
                        )}`,
                    OKText: 'OK',
                })
                .subscribe();
        }
    }

    // #endregion

    // #region Recall Request

    RecallRequest() {
        const self = this;

        // Pop up the dialog box to get a confirmation
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You have about to recall all outstanding referrals related to this request, and return the request` +
                    ` to the initially submitted state. All current employees who have been handling this request will have their ` +
                    `referrals removed, and they will no longer have access to this request.<br /><br />` +
                    `Are you sure you wish to recall this request?`,
                OKText: 'Yes - Recall Request',
                CancelText: 'No - Cancel',
                width: '600px',
                height: '320px',
            })
            .subscribe(result => {
                if (result != null) {
                    // User accepted. Recall the request.
                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Recalling Request...' }).subscribe(() => {
                        self.appSvc.RecallRequest(self.User.AuthToken, self.User.CurrentRole, self.LoadedRequest.RequestID).subscribe(
                            requestID => {
                                // Splash success
                                self.splashSvc.CloseSplash();
                                self.splashSvc.SnackbarSuccess(
                                    `Request #${self.LoadedRequest.FormattedRequestIssueNumber} - Recalled Successfully`
                                );

                                // Recalling a request means we should invalidate the search results cached (if any), since they may
                                // now have invalid data in them about this request.
                                self.LastSearchResults = null;

                                // And, if we're currently tracking this request as the current request (obviously), we must drop that
                                // to prevent the user from coming back to here via the wrong component.
                                self.appSvc.CurrentRequest = null;

                                // If we have a previous page, return to it now.
                                if (self.ParamsPreviousPage) {
                                    self.router.navigate([self.ParamsPreviousPage]);
                                } else {
                                    self.router.navigate(['home']);
                                }
                            },
                            error => self.errorSvc.HandleServiceError(error)
                        );
                    });
                }
            });
    }

    // #endregion

    // #region Redirect Request

    RedirectRequest() {
        const self = this;

        // Pop up the dialog box to get a confirmation
        const options = self.AllCategories.map(c => {
            return <SelectBoxNoteSelectionOption>{
                Text: `${c.CategoryDescription}${
                    c.CabinetMember
                        ? ' (' +
                          self.titleCasePipe.transform(c.CabinetMember.FirstName) +
                          ' ' +
                          self.titleCasePipe.transform(c.CabinetMember.LastName) +
                          ')'
                        : ''
                }`,
                Value: c.CategoryID,
            };
        });

        self.dlgSvc
            .OpenSelectBoxNoteDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You have about to cancel all outstanding referrals related to this request, and select a new category/cabinet` +
                    ` member to initiate a new referral chain. All current employees who have been handling this request will have their ` +
                    `referrals cancelled, and the newly selected category/cabinet member will receive the referral instead.<br /><br />` +
                    `In the spaces provided below, please select the new category to be used for this request, and also provide any ` +
                    `<strong>optional</strong> comments you would like to attach as notes to this referral as it is sent.`,
                Options: options,
                NotePrefix: `Optional comment(s):`,
                OKText: 'Redirect Request',
                CancelText: 'Cancel',
                RequiresNote: false,
                width: '800px',
                height: '500px',
            })
            .subscribe(result => {
                if (result != null) {
                    // User accepted. Pull out the new category and optional note from the result.
                    const category = result.Selection && result.Selection.Value;
                    const message = result.Note || '';

                    // Did the person choose the category that is ALREADY chosen?
                    if (category === self.LoadedRequest.CategoryID) {
                        // Yes - warn and cancel this request to redirect.
                        self.dlgSvc
                            .OpenMessageBoxDialog({
                                TitleText: 'Invalid Category Chosen',
                                MessageText:
                                    `You have selected the same category / cabinet member this request was currently ` +
                                    `referred to. You cannot redirect a request to the currently assigned party. If this was an ` +
                                    `error, please retry your request with a different category / cabinet member.`,
                                OKText: 'OK',
                            })
                            .subscribe();
                        return;
                    }

                    // Update the request by calling to the server to cancel all current referrals and create a new initial referral
                    // to the selected cabinet member (based upon the category chosen).
                    self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Redirecting Request...' }).subscribe(() => {
                        self.appSvc
                            .RedirectRequest(self.User.AuthToken, self.User.CurrentRole, {
                                RequestID: self.LoadedRequest.RequestID,
                                CategoryID: category,
                                Note: message,
                            })
                            .subscribe(
                                request => {
                                    // Splash success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess(
                                        `Request #${self.LoadedRequest.RequestIssueNumber} - Redirected Successfully`
                                    );

                                    // If we have a previous page, return to it now.
                                    if (self.ParamsPreviousPage) {
                                        self.router.navigate([self.ParamsPreviousPage]);
                                    } else {
                                        self.router.navigate(['home']);
                                    }
                                },
                                error => self.errorSvc.HandleServiceError(error)
                            );
                    });
                }
            });
    }

    // #endregion

    // #region Remove File

    RemoveFile(index: number, followup: boolean) {
        const self = this;

        const attfarr = <FormArray>(
            (followup === true ? self.RequestForm.get('FollowUpUploadAttachments') : self.RequestForm.get('ResponseUploadAttachments'))
        );

        if (index == null || index >= attfarr.length) {
            return;
        }

        const entry = attfarr.at(index).value;

        // Get user confirmation prior to removing the file from upload consideration.
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'File Removal Confirmation Needed',
                MessageText: `You are about to remove the attachment named ${entry.File.name.toUpperCase()} from the upload list. Once removed, this file will have to be re-selected in order to be uploaded as part of this response. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove File',
                CancelText: 'No - Cancel',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to remove the file. Process the request.
                    attfarr.removeAt(index);
                }
            });
    }

    // #endregion

    // #region Setup Buttons

    SetupButtons() {
        const self = this;

        const referralStates = ['REFERRED', 'REFTIMEAPP'];
        const nonRedirectableStates = ['ACTIVE', 'CANCELLED', 'COMPLETED', 'DENIED', 'UNAVAIL', 'CLOSEDNONINFO'];

        self.ActionButtons = {
            FacilitatorRecall:
                !nonRedirectableStates.includes(self.LoadedRequest.Status) &&
                ['Facilitator', 'Developer'].includes(self.User.CurrentRole),
            FacilitatorRedirect:
                !nonRedirectableStates.includes(self.LoadedRequest.Status) &&
                ['Facilitator', 'Developer'].includes(self.User.CurrentRole),
            SubmitResponse:
                referralStates.includes(self.LoadedRequest.Status) &&
                (self.LoadedRequest.CurrentReferral || self.LoadedRequest.IsPastOwner),
            PushResponse:
                self.LoadedRequest.Status === 'DONEWINFO' &&
                self.LoadedRequest.ReferralDirection === 'UP' &&
                self.LoadedRequest.CurrentReferral,
            MarkUnavailable:
                referralStates.includes(self.LoadedRequest.Status) &&
                (self.LoadedRequest.CurrentReferral || self.LoadedRequest.IsPastOwner),
            PushUnavailable:
                self.LoadedRequest.Status === 'DONENOINFO' &&
                self.LoadedRequest.ReferralDirection === 'UP' &&
                self.LoadedRequest.CurrentReferral,
            RequestTime:
                referralStates.includes(self.LoadedRequest.Status) &&
                (self.LoadedRequest.CurrentReferral || self.LoadedRequest.IsPastOwner) &&
                ['Cabinet', 'DeputySuperintendent', 'Superintendent', 'Responder'].includes(self.User.CurrentRole),
            PushRequestTime:
                self.LoadedRequest.Status === 'NEEDTIME' &&
                self.LoadedRequest.ReferralDirection === 'UP' &&
                self.LoadedRequest.CurrentReferral &&
                ['Cabinet', 'Responder'].includes(self.User.CurrentRole),
            ForwardReferral:
                referralStates.includes(self.LoadedRequest.Status) &&
                (self.LoadedRequest.CurrentReferral || self.LoadedRequest.IsPastOwner) &&
                (['Cabinet', 'Developer', 'DeputySuperintendent', 'Superintendent'].includes(self.User.CurrentRole) ||
                    (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)),
            SecondForwardReferral:
                self.LoadedRequest.Status === 'REFTIMEAPP' &&
                (self.LoadedRequest.CurrentReferral || self.LoadedRequest.IsPastOwner) &&
                self.LoadedRequest.SecondResponderInfo &&
                self.LoadedRequest.SecondResponderInfo.SAPID &&
                (self.User.CurrentRole === 'Cabinet' ||
                    (self.User.CurrentRole === 'Responder' && self.LoadedRequest.AsDesignee === true)),
            BoardApprove:
                self.LoadedRequest.Status === 'NEEDTIME' &&
                self.LoadedRequest.ReferralDirection === 'UP' &&
                ['Facilitator', 'Developer'].includes(self.User.CurrentRole) &&
                self.LoadedRequest.CurrentReferral,
            BoardDeny:
                self.LoadedRequest.Status === 'NEEDTIME' &&
                self.LoadedRequest.ReferralDirection === 'UP' &&
                ['Facilitator', 'Developer'].includes(self.User.CurrentRole) &&
                self.LoadedRequest.CurrentReferral,
        };

        // Set the response mode. The mode is EDIT for Cabinet, Developer, and Responder roles when the form status is in
        // any of these: REFERRED, REFTIMEAPP, DONEWINFO.
        // The response mode is VIEW for all non-board roles when the status is in: DONEWINFO, COMPLETED
        if (['REFERRED', 'REFTIMEAPP', 'DONEWINFO', 'DONENOINFO'].includes(self.LoadedRequest.Status)) {
            if (['Cabinet', 'Superintendent', 'DeputySuperintendent', 'Developer'].includes(self.User.CurrentRole)) {
                // Might be edit mode, but not if not the current user had a response provided by a designee on their behalf.
                if (self.LoadedRequest.SourceSAPID === self.User.SAPID && self.LoadedRequest.ReferralDirection === 'UP') {
                    self.ResponseMode = 'VIEW';
                } else {
                    self.ResponseMode = 'EDIT';
                }
            } else if (self.User.CurrentRole === 'Responder') {
                self.ResponseMode = self.LoadedRequest.CurrentReferral || self.LoadedRequest.AsDesignee ? 'EDIT' : 'VIEW';
            } else {
                self.ResponseMode = self.User.CurrentRole === 'Facilitator' ? 'VIEW' : null;
            }
        } else if (self.LoadedRequest.Status === 'FOLLOWUP') {
            self.ResponseMode = 'VIEW';
        } else {
            if (self.User.CurrentRole !== 'BoardMember' && self.User.CurrentRole !== 'BoardAssistant') {
                self.ResponseMode = ['DONEWINFO', 'COMPLETED'].includes(self.LoadedRequest.Status) ? 'VIEW' : null;
            } else {
                self.ResponseMode = self.LoadedRequest.Completed || self.LoadedRequest.Cancelled ? 'VIEW' : null;
            }
        }
    }

    // #endregion

    // #region Setup Tracking

    SetupTracking() {
        const self = this;

        self.unsavedChangesSvc.SetTracking(self.CanDeactivate.bind(self));
    }

    // #endregion

    // #region Submit FollowUp Response

    SubmitFollowUpResponse() {
        const self = this;

        if (!self.RequestForm) {
            return;
        }

        const response = self.RequestForm.get('FollowUpResponse').value;

        if (!response) {
            return;
        }

        // Get a confirmation.
        self.dlgSvc
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You are about to submit the following response to the board member's follow-up request:` +
                    `<br /><br /><em>${response}</em><br /><br />Once you submit this response, the board member will be ` +
                    `notified of the response via email and the request will be closed in the system. If you would like ` +
                    `to revise your response, or attach additional files as part of your follow-up request ` +
                    `response, please click the "Canecl" option below and make those changes at this time. Are ` +
                    `you sure you wish to submit your follow-up response at this time?`,
                OKText: 'Yes - Submit Response',
                CancelText: 'No - Cancel',
            })
            .subscribe(_ => {
                if (!_) {
                    return;
                }

                // Send off to the server.
                self.splashSvc
                    .OpenSplash({
                        TitleText: 'Please Wait',
                        MessageText: 'Submitting response...',
                    })
                    .subscribe(__ => {
                        // Send the response to the server. Begin by packing the request object that will be sent to the API.

                        const files = (self.RequestForm.get('FollowUpUploadAttachments') as FormArray).getRawValue();
                        const formData = new FormData();

                        // Store all files in the form data
                        for (const file of files) {
                            formData.append('file', file.File, file.File.name);
                        }

                        // Set the parameters now for the call: AuthToken, CurrentRole, RequestID, FollowUpID, and response.
                        formData.append('AuthToken', self.User.AuthToken || '');
                        formData.append('CurrentRole', self.User.CurrentRole || '');
                        formData.append('RequestID', '' + self.LoadedRequest.RequestID);
                        formData.append('FollowUpID', '' + self.LoadedRequest.FollowUps[0].FollowUpID);
                        formData.append('Note', response);

                        // Set the http Headers
                        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

                        // Create an http-post request and pass the form, while telling the request to report upload
                        // progress.
                        const req = new HttpRequest(
                            'POST',
                            self.configSvc.hostAddress + 'Api/Services/IRIS/AddFollowUpResponse',
                            formData,
                            {
                                //                headers: headers,
                                reportProgress: true,
                            }
                        );

                        // Send the http-post request and subscribe for progress updates on it.
                        self.http.request(req).subscribe(
                            event => {
                                if (event.type === HttpEventType.UploadProgress) {
                                    // Calculate the progress percentage.
                                    const percentDone = Math.round((100 * event.loaded) / event.total);
                                } else if (event instanceof HttpResponse) {
                                    // Set the final file information so the client can update the UI.
                                    const requestInfo = <any>event.body;

                                    // Splash the success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess(`Follow-Up Response Submitted Successfully.`);

                                    // Navigate to previous page
                                    self.router.navigate([self.ParamsPreviousPage || 'home']);
                                }
                            },
                            error => {
                                self.errorSvc.HandleServiceError(error);
                            },
                            () => {}
                        );

                        self.appSvc
                            .AddFollowUpResponse(
                                self.User.AuthToken,
                                self.User.CurrentRole,
                                self.LoadedRequest.RequestID,
                                self.LoadedRequest.FollowUps[0].FollowUpID,
                                response
                            )
                            .subscribe(
                                follows => {
                                    // Drop splash and toast success
                                    self.splashSvc.CloseSplash();
                                    self.splashSvc.SnackbarSuccess('Follow-Up Response Submitted Successfully.');

                                    // Navigate to previous page
                                    self.router.navigate([self.ParamsPreviousPage || 'home']);
                                },
                                error => self.errorSvc.HandleServiceError(error)
                            );
                    });
            });
    }

    // #endregion

    // #region Take Down Tracking

    TakeDownTracking() {
        this.unsavedChangesSvc.ClearTracking();
    }

    // #endregion

    // #region Update Request Form

    UpdateRequestForm(req: RequestInfo) {
        const self = this;

        if (!req) {
            return;
        }

        self.RequestForm.patchValue({
            RequestID: req.RequestID,
            Subject: req.Subject,
            Category: self.AllCategories.find(c => c.CategoryID === req.CategoryID),
            DueDate: req.DueDate,
            Priority: req.Priority,
            Confidential: req.Confidential || false,
            Source: req.Source,
            SourceDescription: req.SourceDescription,
            RequestBoardMember: self.BoardMembers.find(b => b.SAPID === req.RequestedBySAPID),
            SchoolNumber: req.SchoolNumber,
            BoardDistrict: req.BoardDistrict,
            RequestText: req.RequestText,
            RequestIssueNumber: req.RequestIssueNumber,
            RequestedBySAPID: req.RequestorInfo.SAPID,
            SourceNotes: req.SourceNotes,
            ReferralID: req.ReferralID,
            Comments: req.Comments || [],
            EstimatedJobTime: req.EstimatedJobTime,
            ActualJobTime: req.ActualJobTime,
        });

        // If there are request attachments, set them now.
        if (req.RequestAttachments && !!req.RequestAttachments.length) {
            const filearr = <FormGroup[]>[];

            req.RequestAttachments.forEach(att => {
                filearr.push(
                    self.fb.group({
                        FileName: att.FileName,
                        FileSize: att.FileSize,
                    })
                );
            });

            self.RequestForm.setControl('RequestAttachments', self.fb.array(filearr));
        }

        // If there are response attachments, set them now.
        if (req.ResponseAttachments && !!req.ResponseAttachments.length) {
            const filearr = <FormGroup[]>[];

            req.ResponseAttachments.forEach(att => {
                filearr.push(
                    self.fb.group({
                        FileName: att.FileName,
                        FileSize: att.FileSize,
                    })
                );
            });

            self.RequestForm.setControl('ResponseAttachments', self.fb.array(filearr));
        }

        // If there are followup attachments, set them now.
        if (req.FollowUps && req.FollowUps.length) {
            const filearr = <FormGroup[]>[];

            req.FollowUps.forEach(f => {
                if (f.Attachments && f.Attachments.length) {
                    f.Attachments.forEach(att => {
                        filearr.push(
                            self.fb.group({
                                FileName: att.FileName,
                                FileSize: att.FileSize,
                            })
                        );
                    });
                }
            });

            self.RequestForm.setControl('FollowUpAttachments', self.fb.array(filearr));
        }

        // Set up the comments data source
        self.CommentsData.data = req.Comments || [];
        self.CommentsNoData = !self.CommentsData.data || !self.CommentsData.data.length;
        self.CommentsVisibleHeaders = self.SmallSize
            ? ['SmallSize']
            : ['DateCommentAdded', 'Comment', 'CommentCreatorFullName', 'CommentRecipientFullName'];
        self.CommentsVisibleColumns = self.CommentsNoData ? ['NoData'] : self.CommentsVisibleHeaders.slice();

        // Set whether or not actions can be taken. If the referral is in the status of REFERRED or REFTIMEAPP, actions
        // can be taken. If not, we can't take actions.
        self.CanTakeActions = ['REFERRED', 'REFTIMEAPP'].includes(req.Status);

        self.ResponseTabIndex = 2 + (self.LoadedRequest.Comments && !!self.LoadedRequest.Comments.length ? 1 : 0);
    }

    // #endregion
}

// #region Custom Classes

// #region Action Buttons Class

export class ActionButtons {
    FacilitatorRecall?: boolean;
    FacilitatorRedirect?: boolean;
    SubmitResponse?: boolean;
    PushResponse?: boolean;
    MarkUnavailable?: boolean;
    PushUnavailable?: boolean;
    RequestTime?: boolean;
    PushRequestTime?: boolean;
    BoardApprove?: boolean;
    BoardDeny?: boolean;
    ForwardReferral?: boolean;
    SecondForwardReferral?: boolean;
}

// #endregion

// #endregion
